<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { createProduct } from '../productService'
import type { EngineeringProduct, ProductPayload } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  productType: ProductPayload['type']
}>()

const emit = defineEmits<{
  close: []
  created: [product: EngineeringProduct]
}>()

const { t } = useI18n()
const client = useApiClient()
const saving = ref(false)
const errorMessage = ref('')
const form = reactive({
  name: '',
  sku: '',
  unit: 'pcs',
})

function reset(): void {
  form.name = ''
  form.sku = ''
  form.unit = 'pcs'
  errorMessage.value = ''
}

watch(() => props.open, (open) => {
  if (open) reset()
}, { immediate: true })

async function submit(): Promise<void> {
  saving.value = true
  errorMessage.value = ''
  try {
    const product = await createProduct(client, {
      name: form.name.trim(),
      sku: form.sku.trim(),
      type: props.productType,
      unit: form.unit.trim(),
    })
    emit('created', product)
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-backdrop" @click.self="emit('close')">
      <section class="create-modal" role="dialog" aria-modal="true" :aria-label="title">
        <header>
          <div>
            <p class="eyebrow">{{ t('engineering.picker.repositoryTool') }}</p>
            <h3>{{ title }}</h3>
          </div>
          <button class="close-button" type="button" :aria-label="t('engineering.picker.closeCreate')" @click="emit('close')">×</button>
        </header>
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <label>
          <span>{{ t('engineering.picker.name') }}</span>
          <input v-model="form.name" type="text" :placeholder="t('engineering.picker.name')">
        </label>
        <label>
          <span>SKU</span>
          <input v-model="form.sku" type="text" placeholder="SKU">
        </label>
        <label>
          <span>{{ t('engineering.picker.unit') }}</span>
          <input v-model="form.unit" type="text" placeholder="pcs">
        </label>
        <footer>
          <button class="secondary-button" type="button" @click="emit('close')">{{ t('engineering.picker.cancel') }}</button>
          <button type="button" :disabled="saving || !form.name.trim() || !form.sku.trim() || !form.unit.trim()" @click="submit">
            {{ saving ? t('engineering.picker.saving') : t('engineering.picker.create') }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop { position: fixed; z-index: 1100; inset: 0; display: grid; padding: 24px; background: rgb(5 31 67 / 52%); place-items: center; }
.create-modal { display: grid; width: min(460px, 100%); gap: 12px; padding: 20px; border: 1px solid #cbd7e5; background: #fff; box-shadow: 0 24px 60px rgb(5 31 67 / 25%); }
header, footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.eyebrow { margin: 0 0 4px; color: #66726e; font-size: 11px; font-weight: 800; text-transform: uppercase; }
h3, p { margin: 0; }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
input { padding: 11px 12px; border: 1px solid #aebdcc; font: inherit; }
.close-button { padding: 2px 10px; color: #66726e; background: transparent; font-size: 28px; }
.secondary-button, footer button:last-child { padding: 8px 12px; border: 1px solid #b9c5c1; font: inherit; font-weight: 700; cursor: pointer; }
.secondary-button { color: #285b9a; background: #fff; }
footer button:last-child { color: #fff; background: #285b9a; }
.error-message { color: #852d22; }
</style>
